/* eslint-disable max-len */
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ConsultantFinderComponent } from '@consultant/consultant-finder/consultant-finder.component';
import { FindConsultantRequest, LocateConsultantRequest } from '@core/dto/consultant.dto';
import { ConsultantFinderType } from '@core/enums/consultant-finder-type.enum';
import { ConsultantLocatorErrorType } from '@core/enums/consultant-locator-error-type.enum';
import { Locale } from '@core/enums/locale';
import { ModalConfig } from '@core/models/modal-config.model';
import { AppState } from '@core/store';
import {
  selectConsultantFinder,
  selectConsultantFinderLoading,
  selectConsultantLocatorErrorType,
} from '@core/store/consultant';
import {
  locateConsultant,
  resetConsultantLocatorErrorType,
} from '@core/store/consultant/consultant.actions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { CommonModal } from '@shared/interfaces/common-modal.interface';
import { isMexEnv } from '@shared/utils/environment-utils';
import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-consultant-finder-modal',
  templateUrl: './consultant-finder-modal.component.html',
  styleUrls: ['./consultant-finder-modal.component.scss'],
})
export class ConsultantFinderModalComponent implements OnInit, OnDestroy {
  @Input() zipCode: string;
  @Input() hasAutomaticAssignOption: boolean = false;
  @Input() mandatory: boolean = false;
  @Input() showExtraHint: boolean = false;
  @Input()
  consultantFinderText: string = $localize`Please find a consultant using the below options.`;
  @Output() consultantSelected: EventEmitter<void> = new EventEmitter<void>();
  @Output() backToPersonalInfoPage: EventEmitter<void> = new EventEmitter<void>();

  readonly LocatorErrorTypes = ConsultantLocatorErrorType;
  consultantLocatorError$: Observable<ConsultantLocatorErrorType>;
  loading$: Observable<boolean>;
  finderType: ConsultantFinderType;
  loadMoreButtonIsVisible: boolean;
  consultantFinderType = ConsultantFinderType;
  finderIsValid: boolean = false;
  filter: FindConsultantRequest | LocateConsultantRequest;
  skipFinderModal: boolean = false;
  nextStepListener: Subscription;

  optionsConfig: ModalConfig;
  optionsConfigMX: ModalConfig;
  finderConfig: ModalConfig;
  resultConfig: ModalConfig;
  currentModal: CommonModal;

  readonly extraHint = {
    [ConsultantFinderType.find]: $localize`If you know a Consultant we can help connect you to their website for future shopping.`,
    [ConsultantFinderType.locate]: $localize`If you don't know a Consultant, we can help connect you with one for future shopping.`,
  };
  readonly finderTitleText = $localize`Find consultant`;
  readonly finderTitleLocateText = $localize`Locate a consultant`;
  readonly isMexEnv = isMexEnv;

  @ViewChild('confirmModal') private confirmModal: ConfirmModalComponent;
  @ViewChild('optionsModal') private optionsModal: ModalComponent;
  @ViewChild('optionsModalMX') private optionsModalMX: ModalComponent;
  @ViewChild('finderModal') private finderModal: ModalComponent;
  @ViewChild('resultModal') private resultModal: ModalComponent;
  @ViewChild('consultantFinder') private consultantFinder: ConsultantFinderComponent;

  constructor(
    private store$: Store<AppState>,
    @Inject(LOCALE_ID) private localeId: Locale,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.initModalConfigs();

    this.consultantLocatorError$ = this.store$.select(selectConsultantLocatorErrorType);
    this.loading$ = this.store$.select(selectConsultantFinderLoading);
  }

  ngOnDestroy(): void {
    this.nextStepListener?.unsubscribe();
  }

  open() {
    if (isMexEnv) {
      this.currentModal = this.confirmModal;
      this.confirmModal.open();
    } else {
      this.optionsModal.open();
    }
  }

  noConfirmModal() {
    this.currentModal.close();
    this.currentModal = this.optionsModalMX;
    this.optionsModalMX.open();
  }

  confirmModalCloseClick() {
    this.backToPersonalInfoPage.emit();
    this.confirmModal.close();
  }

  openFinder(type: ConsultantFinderType, loadMoreButtonIsVisible: boolean) {
    if (isMexEnv) {
      if (type == this.consultantFinderType.locate) {
        this.finderConfig.title = this.finderTitleLocateText;
      } else {
        this.finderConfig.title = this.finderTitleText;
      }
      this.currentModal.close();
    } else {
      this.optionsModalClose();
    }
    this.skipFinderModal = false;
    this.finderType = type;
    this.loadMoreButtonIsVisible = loadMoreButtonIsVisible;
    this.currentModal = this.finderModal;
    this.finderModal.open();
  }

  closeFinder() {
    this.currentModal.close();
    this.finderModal.close();
    if (isMexEnv) {
      if (this.finderType == this.consultantFinderType.locate) {
        this.optionsModalMX.open();
      } else {
        this.confirmModal.open();
      }
    } else {
      this.optionsModal.open();
    }
  }

  dismissOptionsModalMX() {
    this.optionsModalMX.close();
    this.modalService.dismissAll();
    this.confirmModal.open();
  }
  search() {
    if (isMexEnv) {
      this.currentModal.close();
    } else {
      this.finderModal.close();
    }
    this.consultantFinder.submit();
    this.resultModal.open();
  }

  resultBack() {
    this.resultModal.close();
    this.currentModal.close();
    if (this.skipFinderModal) {
      if (isMexEnv) {
        this.optionsModalMX.open();
      } else {
        this.optionsModal.open();
      }
    } else {
      this.finderModal.open();
    }
  }

  onFinderValidChange(valid: boolean) {
    this.finderIsValid = valid;
  }

  onFinderSubmitted(value: FindConsultantRequest | LocateConsultantRequest) {
    this.filter = value;
  }

  notNow() {
    this.optionsModalClose();
  }

  autoAssign() {
    this.loadMoreButtonIsVisible = false;
    this.skipFinderModal = true;
    this.store$.dispatch(
      locateConsultant({
        payload: { zipCode: this.zipCode, cultureCode: this.localeId, pageNumber: 1, pageSize: 1 },
      }),
    );
    this.listenNextStep();
  }

  closeResult() {
    this.resultModal.close();
    this.currentModal.close();
    if (isMexEnv) {
      this.confirmModal.close();
    }
    this.consultantSelected.emit();
  }

  private initModalConfigs() {
    this.optionsConfig = {
      title: $localize`Assign consultant`,
      hideCloseButton: true,
      hideDismissButton: true,
      hideHeaderDismissButton: this.mandatory,
      ngbModalOptions: {
        size: 'lg',
        modalDialogClass: 'modal',
        backdrop: 'static',
        keyboard: !this.mandatory,
      },
    };

    this.finderConfig = {
      title: this.finderTitleText,
      hideCloseButton: true,
      hideDismissButton: true,
      hideHeaderDismissButton: this.mandatory,
      ngbModalOptions: {
        size: 'lg',
        modalDialogClass: 'modal',
        backdrop: 'static',
        keyboard: !this.mandatory,
      },
    };

    this.resultConfig = {
      title: isMexEnv ? $localize`Search Result(s)` : $localize`Consultant(s)`,
      hideCloseButton: true,
      hideDismissButton: true,
      hideHeaderDismissButton: this.mandatory,
      ngbModalOptions: {
        size: 'lg',
        modalDialogClass: 'modal',
        backdrop: 'static',
        keyboard: !this.mandatory,
      },
    };
    this.optionsConfigMX = {
      title: $localize`Help me find a Consultant`,
      hideCloseButton: true,
      hideDismissButton: true,
      hideHeaderDismissButton: false,
      onDismissHeader: this.dismissOptionsModalMX.bind(this),
      ngbModalOptions: {
        size: 'lg',
        modalDialogClass: 'modal',
        backdrop: 'static',
        keyboard: !this.mandatory,
      },
    };
  }

  private listenNextStep(): void {
    this.nextStepListener = this.store$
      .select(selectConsultantFinder)
      .pipe(
        filter((finder) => !!finder.results.length),
        take(1),
      )
      .subscribe(() => {
        if (isMexEnv) {
          this.optionsModalMXClose();
        } else {
          this.optionsModalClose();
        }
        this.resultModal.open();
      });
  }

  private optionsModalClose(): void {
    this.nextStepListener?.unsubscribe();
    this.store$.dispatch(resetConsultantLocatorErrorType());
    this.optionsModal.close();
  }

  private optionsModalMXClose(): void {
    this.nextStepListener?.unsubscribe();
    this.store$.dispatch(resetConsultantLocatorErrorType());
    this.optionsModalMX.close();
  }
}
